import React from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { AgnesInput, AgnesButton } from '@agnes/ui';
import { Building2, Mail, Lock, Eye, EyeOff, ChevronDown } from 'lucide-react';
import ServiceAuthSSO from '@/services/ServiceAuthSSO';
import { notify } from '@/stores/useToast';
import { usePermissionsStore } from '@/stores/usePermissionsStore';

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-1.5 block text-sm font-semibold text-agnes-text-primary">{children}</span>
  );
}

interface TenantOption {
  slug: string;
  display_name: string;
}

// Prod (flag de build): tenant vem do path /:tenant_slug/login e o seletor some.
const TENANT_IN_PATH = import.meta.env.VITE_TENANT_IN_PATH === 'true';

function LoginForm({ tenant_slug }: Readonly<{ tenant_slug?: string }>) {
  const navigate = useNavigate();
  const [tenants, setTenants] = React.useState<TenantOption[]>([]);
  const [selectedOrg, setOrg] = React.useState('');
  const org = TENANT_IN_PATH ? (tenant_slug ?? '') : selectedOrg;
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPass, setShowPass] = React.useState(false);
  const [remember, setRemember] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  // 2FA (fase 2): quando o login devolve session_token, entra no step de código.
  const [step, setStep] = React.useState<'login' | '2fa'>('login');
  const [sessionToken, setSessionToken] = React.useState('');
  const [maskedEmail, setMaskedEmail] = React.useState('');
  const [code, setCode] = React.useState('');
  const [resending, setResending] = React.useState(false);

  React.useEffect(() => {
    if (TENANT_IN_PATH) return;
    ServiceAuthSSO.listTenants()
      .then((list: TenantOption[]) =>
        setTenants([...list].sort((a, b) => a.display_name.localeCompare(b.display_name, 'pt-BR'))),
      )
      .catch(() => setTenants([]));
  }, []);

  function completeSession(data: any) {
    // remember (checkbox "Manter-me conectado"): true -> localStorage (persiste ao
    // fechar o navegador); false -> sessionStorage (sessão morre ao fechar).
    ServiceAuthSSO.persistSession(data, org, remember);
    // Limpa permissões cacheadas de uma sessão anterior nesta aba (SPA sem reload).
    usePermissionsStore.getState().reset();
    navigate('/home');
  }

  function toastError(err: any, fallback: string) {
    notify.error(err?.response?.data?.detail ?? err?.response?.data?.message ?? fallback);
  }

  // Retorna true se resolveu (navegou com tokens ou entrou no step 2FA).
  function applyLoginResult(data: any): boolean {
    if (data?.access_token) {
      completeSession(data);
      return true;
    }
    if (data?.session_token) {
      // Sentinela "__password__": o KC (SPI OTP do lion) espera a SENHA como
      // otp_session_token no verify-2fa. Troca o sentinela pela senha real (paridade lion).
      setSessionToken(data.session_token === '__password__' ? password : data.session_token);
      setMaskedEmail(data.masked_email || '');
      setStep('2fa');
      return true;
    }
    return false;
  }

  async function onEntrar(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await ServiceAuthSSO.login(org, email, password);
      if (!applyLoginResult(data)) notify.error('Resposta inesperada do servidor de autenticação.');
    } catch (err) {
      toastError(err, 'Falha no login. Verifique as credenciais.');
    } finally {
      setLoading(false);
    }
  }

  async function onVerificar(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await ServiceAuthSSO.verify2fa(org, email, password, sessionToken, code);
      completeSession(data);
    } catch (err) {
      toastError(err, 'Código inválido ou expirado.');
    } finally {
      setLoading(false);
    }
  }

  async function onReenviar() {
    if (resending) return; // anti duplo-clique — evita disparar vários OTPs
    setResending(true);
    try {
      const data = await ServiceAuthSSO.login(org, email, password);
      if (data?.access_token) {
        applyLoginResult(data);
      } else if (data?.session_token) {
        setSessionToken(data.session_token === '__password__' ? password : data.session_token);
        setMaskedEmail(data.masked_email || '');
        notify.success('Código reenviado.');
      } else {
        notify.error('Resposta inesperada do servidor de autenticação.');
      }
    } catch (err) {
      toastError(err, 'Falha ao reenviar o código.');
    } finally {
      setResending(false);
    }
  }

  const podeEntrar = org.trim() !== '' && email.trim() !== '' && password !== '' && !loading;

  if (step === '2fa') {
    return (
      <form onSubmit={onVerificar} className="flex flex-col gap-4">
        <div>
          <h2 className="text-2xl font-bold text-agnes-text-primary">Verificação em duas etapas</h2>
          <p className="mt-1 text-sm text-agnes-text-muted">
            Digite o código enviado {maskedEmail ? `para ${maskedEmail}` : 'ao seu e-mail'}.
          </p>
        </div>
        <div>
          <Label>Código</Label>
          <AgnesInput
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="000000"
            inputMode="numeric"
            autoComplete="one-time-code"
            startIcon={<Lock className="h-4 w-4" />}
          />
        </div>
        <AgnesButton type="submit" variant="primary" className="w-full" disabled={!code || loading}>
          {loading ? 'Verificando…' : 'Verificar'}
        </AgnesButton>
        <div className="flex items-center justify-between text-sm">
          <button
            type="button"
            className="text-agnes-primary disabled:opacity-50"
            onClick={onReenviar}
            disabled={resending}
          >
            {resending ? 'Reenviando…' : 'Reenviar código'}
          </button>
          <button
            type="button"
            className="text-agnes-text-muted"
            onClick={() => {
              setStep('login');
              setCode('');
            }}
          >
            Voltar
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={onEntrar} className="flex flex-col gap-4">
      <div>
        <h2 className="text-2xl font-bold text-agnes-text-primary">Acesse sua conta</h2>
        <p className="mt-1 text-sm text-agnes-text-muted">
          Informe seus dados para entrar no sistema.
        </p>
      </div>

      {!TENANT_IN_PATH && (
        <div>
          <Label>Organização</Label>
          <div className="relative">
            <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-agnes-text-muted" />
            <select
              data-testid="tenant-select"
              value={org}
              onChange={(e) => setOrg(e.target.value)}
              className={`w-full appearance-none rounded-lg border border-agnes-border bg-agnes-bg-surface py-2.5 pl-10 pr-10 text-sm outline-none transition-colors focus:border-agnes-primary ${org ? 'text-agnes-text-primary' : 'text-agnes-text-muted'}`}
            >
              <option value="" disabled>
                Selecione a organização
              </option>
              {tenants.map((t) => (
                <option key={t.slug} value={t.slug} className="text-agnes-text-primary">
                  {t.display_name}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-agnes-text-muted" />
          </div>
        </div>
      )}

      <div>
        <Label>E-mail corporativo</Label>
        <AgnesInput
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="seu@email.com"
          autoComplete="username"
          startIcon={<Mail className="h-4 w-4" />}
        />
      </div>

      <div>
        <Label>Senha</Label>
        <AgnesInput
          type={showPass ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Digite sua senha"
          autoComplete="current-password"
          startIcon={<Lock className="h-4 w-4" />}
          endIcon={
            <button
              type="button"
              aria-label={showPass ? 'Ocultar senha' : 'Mostrar senha'}
              onClick={() => setShowPass((s) => !s)}
              className="text-agnes-text-muted hover:text-agnes-text-primary"
            >
              {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          }
        />
      </div>

      <div className="flex items-center justify-between text-sm">
        <label className="flex cursor-pointer items-center gap-2 text-agnes-text-muted">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
          />{' '}
          Manter-me conectado
        </label>
        <Link to="/recuperar-senha" className="font-medium text-agnes-primary">
          Esqueci minha senha
        </Link>
      </div>

      <AgnesButton
        type="submit"
        variant="primary"
        className="w-full"
        disabled={!podeEntrar}
        startIcon={<Lock className="h-4 w-4" />}
      >
        {loading ? 'Acessando…' : 'Acessar sistema'}
      </AgnesButton>
    </form>
  );
}

// Slugs que são rotas do app, não tenants (ex.: /login/login).
const RESERVED_SLUGS = ['login', 'recuperar-senha'];

export default function PageLogin() {
  const { tenant_slug } = useParams();
  const slug = tenant_slug && !RESERVED_SLUGS.includes(tenant_slug) ? tenant_slug : undefined;
  // key: trocar de /a/login para /b/login remonta o form (não carrega senha/2FA do tenant anterior).
  return <LoginForm key={slug} tenant_slug={slug} />;
}
